// Auto-link calculator names in blog HTML to /calc/:slug
// Scans article content after render and wraps recognized calculator names in styled buttons.
// Only wraps the FIRST occurrence per slug per article to avoid noise.

import { CATEGORIES } from '../data/calculators';
import { CALC_REGISTRY } from '../calculators/registry';

// Build a lookup once: [{ pattern, slug, name }]
let CACHE = null;
function buildLookup() {
  if (CACHE) return CACHE;
  const list = [];
  const seenSlug = new Set();
  for (const cat of CATEGORIES) {
    for (const tool of cat.tools) {
      if (!CALC_REGISTRY[tool.slug]) continue;
      if (seenSlug.has(tool.slug)) continue;
      seenSlug.add(tool.slug);
      // Common name variants to match, longest first for greedy matching
      const variants = new Set();
      variants.add(tool.name);
      variants.add(tool.name.replace(/\s+Calculator$/, ' Calculator'));
      // Also match shortened form without trailing "Calculator" if unambiguous
      const short = tool.name.replace(/\s+Calculator$/, '').trim();
      if (short.length > 4 && short.split(' ').length >= 2) {
        variants.add(short + ' calculator');
      }
      variants.forEach(v => list.push({ text: v, slug: tool.slug, name: tool.name }));
    }
  }
  // Sort longest-first so "Mortgage Payoff Calculator" beats "Mortgage Calculator"
  list.sort((a, b) => b.text.length - a.text.length);
  CACHE = list;
  return list;
}

// Escape user text for RegExp
function esc(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

/**
 * autoLinkCalculators(rootEl)
 * Walks text nodes inside rootEl and replaces matched calculator names
 * with <a class="calc-inline-link" href="/calc/:slug">…</a>.
 * Only wraps the first hit per slug per article.
 * Skips text inside <a>, <code>, <pre>, <h1>–<h6> to avoid ugliness.
 */
export function autoLinkCalculators(rootEl) {
  if (!rootEl) return 0;
  // Prevent double-processing (React StrictMode double-invokes effects in dev)
  if (rootEl.dataset.calcLinked === '1') return 0;
  rootEl.dataset.calcLinked = '1';
  const lookup = buildLookup();
  const used = new Set();
  const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const p = node.parentElement;
      if (!p) return NodeFilter.FILTER_REJECT;
      const tag = p.tagName;
      if (['A', 'CODE', 'PRE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'SCRIPT', 'STYLE'].includes(tag)) return NodeFilter.FILTER_REJECT;
      if (p.closest('a')) return NodeFilter.FILTER_REJECT;
      return node.nodeValue && node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    }
  });
  const textNodes = [];
  let n;
  while ((n = walker.nextNode())) textNodes.push(n);

  let replaced = 0;
  for (const textNode of textNodes) {
    let text = textNode.nodeValue;
    // Find best match in this text node (longest, unused)
    let hit = null;
    for (const item of lookup) {
      if (used.has(item.slug)) continue;
      const re = new RegExp('\\b' + esc(item.text) + '\\b', 'i');
      const m = text.match(re);
      if (m) { hit = { ...item, index: m.index, matched: m[0], regex: re }; break; }
    }
    if (!hit) continue;

    const before = text.slice(0, hit.index);
    const after = text.slice(hit.index + hit.matched.length);
    const parent = textNode.parentNode;

    if (before) parent.insertBefore(document.createTextNode(before), textNode);
    const a = document.createElement('a');
    a.href = '/calc/' + hit.slug;
    a.className = 'calc-inline-link';
    a.setAttribute('data-calc-link', hit.slug);
    a.textContent = hit.matched;
    parent.insertBefore(a, textNode);
    if (after) parent.insertBefore(document.createTextNode(after), textNode);
    parent.removeChild(textNode);
    used.add(hit.slug);
    replaced++;
    if (replaced >= 8) break; // cap per article
  }
  return replaced;
}
